<template>
    <Layout class="page">
        <Header class="header">
            <img src="/assemble_logo.svg" alt="assemble logo" />
        </Header>

        <LoadingLayer v-if="isLoading" is-opaque :left-offset-pixels="60" size="large" />

        <hox-empty-message v-else-if="isError || !bannerPreviewData.length" type="bug" class="network-error">
            <template #title>We were unable to retrieve any templates.</template>
            <template #content>The link may have expired or an error occurred.</template>
        </hox-empty-message>

        <Layout v-else class="main">
            <Sider :width="300" class="sidebar">
                <div class="sidebar-content">
                    <h2 class="sidebar-content__title">Shared creatives</h2>

                    <Divider />

                    <div class="sidebar-content__preview-toggle">
                        <SocialPreviewToggle v-model="showMetaPreview" />
                    </div>

                    <div class="variants">
                        <div class="sidebar-content__top-controls">
                            <p v-if="variantsToDisplay.length === bannerPreviewData.length">
                                {{
                                    bannerPreviewData.length === 1
                                        ? "Showing 1 creative"
                                        : `Showing all ${bannerPreviewData.length} creatives`
                                }}
                            </p>
                            <p v-else>
                                Showing {{ variantsToDisplay.length }} of {{ bannerPreviewData.length }} creatives
                            </p>
                            <ButtonGroup>
                                <Button
                                    type="default"
                                    size="small"
                                    icon="ios-apps"
                                    class="switch-view-button"
                                    :class="{ selected: viewMode === VIEWS.MULTIPLE }"
                                    @click="handleMultipleView"
                                />
                                <Button
                                    type="default"
                                    size="small"
                                    icon="ios-square-outline"
                                    class="switch-view-button"
                                    :class="{ selected: viewMode === VIEWS.SINGLE }"
                                    @click="handleSingleView"
                                />
                            </ButtonGroup>
                        </div>

                        <Divider />

                        <ul class="variants-list">
                            <li
                                v-for="preview in bannerPreviewData"
                                :key="preview.id"
                                class="reporting-label"
                                :class="{ selected: selectedVariant && selectedVariant.id === preview.id }"
                                @click="selectVariant(preview.id)"
                            >
                                {{ preview.reportingLabel }}
                            </li>
                        </ul>
                    </div>
                </div>
            </Sider>

            <Space ref="body" align="start" wrap :size="[15, 15]" class="banners">
                <ShareBannerPreview
                    v-for="preview in variantsToDisplay"
                    :key="preview.id"
                    :banner-preview-data="preview"
                    :max-width="bodyDimensionsPixels.width"
                    :max-height="bodyDimensionsPixels.height"
                    :scaling-factor="scalingFactor"
                    :show-meta-preview="showMetaPreview"
                    playback-namespace="previews"
                />
            </Space>
        </Layout>
    </Layout>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";
import { useRoute } from "vue-router";

import generateShortId from "shared-utils/utils/generateShortId";

import type { BannerPreviewData } from "@/types/types";

import ShareBannerPreview from "@/components/Campaign/ShareBannerPreview.vue";
import LoadingLayer from "@/components/common/LoadingLayer.vue";
import SocialPreviewToggle from "@/components/Campaign/SocialPreviewToggle.vue";
import { API_ROUTES } from "@/constants";

import { useScalingBannerWrapper } from "@/composables/useScalingBannerWrapper";

const VIEWS = {
    MULTIPLE: "multiple",
    SINGLE: "single"
} as const;

const route = useRoute();

const bodyRef = useTemplateRef("body");
const { bodyDimensionsPixels, scalingFactor, setBodyDimensionsPixels } = useScalingBannerWrapper({
    bodyRef,
    bodyWidthOffset: -64
});

const viewMode = ref<(typeof VIEWS)[keyof typeof VIEWS]>(VIEWS.MULTIPLE);
const selectedVariant = ref<BannerPreviewData | null>(null);
const isLoading = ref<boolean>(true);
const isError = ref<boolean>(false);
const showMetaPreview = ref<boolean>(false);

const bannerPreviewData = ref<BannerPreviewData[]>([]);

const variantsToDisplay = computed(() => {
    if (viewMode.value === VIEWS.SINGLE && selectedVariant.value) {
        return [selectedVariant.value];
    }

    return bannerPreviewData.value;
});

function handleSingleView() {
    if (bannerPreviewData.value.length > 0 && bannerPreviewData.value[0]) {
        selectedVariant.value = bannerPreviewData.value[0];
    }

    viewMode.value = VIEWS.SINGLE;
}

function handleMultipleView() {
    selectedVariant.value = null;
    viewMode.value = VIEWS.MULTIPLE;
}

function selectVariant(id: string) {
    selectedVariant.value = bannerPreviewData.value.find(preview => preview.id === id)!;
    viewMode.value = VIEWS.SINGLE;
}

async function getBannerData() {
    if (!route.params.shareKey) {
        console.error("Missing share key param");

        isError.value = true;
        isLoading.value = false;

        return;
    }

    try {
        const resp = await fetch(`${API_ROUTES.SHARE}/${route.params.shareKey}`, {
            method: "GET",
            headers: {
                accept: "application/json",
                "content-type": "application/json"
            }
        });

        const shareData = (await resp.json()) as Omit<BannerPreviewData, "id">[];

        bannerPreviewData.value = shareData.map(preview => ({
            ...preview,
            id: generateShortId()
        }));

        isLoading.value = false;

        setBodyDimensionsPixels();
    } catch {
        isError.value = true;
        isLoading.value = false;
    }
}

onMounted(() => {
    (window as unknown as EventTarget).addEventListener("resize", setBodyDimensionsPixels, true);

    getBannerData();
});

onBeforeUnmount(() => {
    (window as unknown as EventTarget).removeEventListener("resize", setBodyDimensionsPixels, true);
});
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.page {
    height: 100vh;
    max-height: 100vh;
    overflow: hidden;

    iframe {
        pointer-events: none;
    }

    .network-error {
        margin-top: 48px;
    }
}

.sidebar {
    height: calc(100vh - 60px);
    background-color: $white;
    overflow: auto;

    .sidebar-content {
        padding: 24px 0;
        width: 100%;
        max-width: 100%;

        display: flex;
        flex-direction: column;
    }

    .sidebar-content__title {
        margin-left: 24px;
    }

    .sidebar-content__preview-toggle {
        padding: 0 24px 16px;
    }

    .variants {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 0 24px;

        ul li {
            margin: 0;
            padding: 0;
            text-indent: 0;
            list-style-type: none;
        }

        .sidebar-content__top-controls {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .switch-view-button {
                padding: 13px;
                &.selected {
                    background-color: var(--wpp-primary-color-500);
                    color: $white;
                }
            }
        }

        .reporting-label {
            word-wrap: break-word;
            word-break: break-all;
            font-weight: 600;
            padding: 2px 5px;
            border-radius: 5px;
            transition: all 0.3s ease;
            cursor: pointer;
            font-size: 13px;

            margin-bottom: 16px;

            &:hover,
            &.selected {
                background-color: var(--wpp-grey-color-300);
            }
        }
    }
}

.header {
    display: flex;
    align-items: center;
    background-color: $white;
    padding: 10px 24px;
    border-bottom: 1px solid rgba(170, 170, 170, 0.605);
    height: 60px;
}

.main {
    display: grid;
    grid-template-columns: auto 1fr;
}

.banners {
    max-height: calc(100vh - 60px);
    overflow: auto;
    padding: 32px;
}
</style>
